<template>
  <div class="argon-lookup-container" v-click-outside="closeResults">
    <div class="input-group">
      <span class="input-group-text">
        <i class="fas fa-search"></i>
      </span>
      <input
        type="text"
        class="form-control"
        :class="{ 'is-invalid': !internalSelected && searchTerm && !filteredOptions.length }"
        v-model="searchTerm"
        @input="handleInput"
        @keydown.down.prevent="navigateResults('down')"
        @keydown.up.prevent="navigateResults('up')"
        @keydown.enter.prevent="selectHighlighted"
        @focus="openResults"
        :placeholder="placeholder"
      />
      <button 
          v-if="internalSelected" 
          class="btn btn-sm btn-outline-danger mb-0" 
          @click="clearSelection"
          type="button"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div v-if="showResults && filteredOptions.length" class="results-dropdown shadow-sm card">
      <ul class="list-group list-group-flush">
        <li
          v-for="(option, index) in filteredOptions"
          :key="option[trackBy]"
          class="list-group-item list-group-item-action cursor-pointer"
          :class="{ 'bg-gray-200': index === highlightIndex }"
          @click="selectOption(option)"
          @mouseenter="highlightIndex = index"
        >
          <slot name="option" :option="option">
            {{ option[displayKey] }}
          </slot>
        </li>
      </ul>
    </div>

    <div v-if="showResults && searchTerm.length >= minChars && !filteredOptions.length" class="results-dropdown shadow-sm card">
      <div class="p-2 text-center text-muted text-sm">
        No se encontraron coincidencias.
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

// Directiva personalizada v-click-outside para Vue 3
const clickOutside = {
  beforeMount: (el, binding) => {
    el.clickOutsideEvent = (event) => {
      // Si el clic no fue en el elemento ni en sus descendientes
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event, el);
      }
    };
    document.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted: (el) => {
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};

export default {
  name: 'ArgonLookup',
  directives: { clickOutside },
  props: {
    // La lista completa de opciones a buscar (productos, clientes, etc.)
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    // El valor actualmente seleccionado (objeto completo)
    modelValue: {
      type: [Object, null],
      default: null
    },
    // La propiedad del objeto a mostrar en la lista y en el input (ej. 'nombre', 'sku', 'display_name')
    displayKey: {
      type: String,
      default: 'name'
    },
    // La propiedad para el trackeo (ej. 'id')
    trackBy: {
        type: String,
        default: 'id'
    },
    // Mínimo de caracteres para iniciar la búsqueda
    minChars: {
      type: Number,
      default: 2
    },
    placeholder: {
      type: String,
      default: 'Escriba para buscar...'
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const internalSelected = ref(props.modelValue);
    const searchTerm = ref('');
    const showResults = ref(false);
    const highlightIndex = ref(-1);

    // --- PROPIEDADES CALCULADAS ---

    const filteredOptions = computed(() => {
      if (!searchTerm.value || searchTerm.value.length < props.minChars) return [];
      const searchLower = searchTerm.value.toLowerCase();
      
      return props.options.filter(option => {
        const value = option[props.displayKey] ? String(option[props.displayKey]).toLowerCase() : '';
        return value.includes(searchLower);
      }).slice(0, 8);
    });
    
    // --- WATCHERS ---

    // Sincroniza el modelo externo con el valor interno y el input
    watch(() => props.modelValue, (newVal) => {
        internalSelected.value = newVal;
        if (newVal) {
            searchTerm.value = String(newVal[props.displayKey]);
        } else if (!showResults.value) {
            searchTerm.value = '';
        }
    }, { immediate: true });

    // --- MÉTODOS DE BÚSQUEDA Y SELECCIÓN ---

    const selectOption = (option) => {
      internalSelected.value = option;
      searchTerm.value = String(option[props.displayKey]);
      emit('update:modelValue', option);
      showResults.value = false;
      highlightIndex.value = -1;
    };

    const clearSelection = () => {
      internalSelected.value = null;
      searchTerm.value = '';
      emit('update:modelValue', null);
      showResults.value = false;
    };

    const handleInput = () => {
        // Al escribir, si el valor actual ya no coincide, limpiamos la selección
        if (internalSelected.value && searchTerm.value !== String(internalSelected.value[props.displayKey])) {
            internalSelected.value = null;
            emit('update:modelValue', null);
        }
        
        if (searchTerm.value.length >= props.minChars) {
             showResults.value = true;
             highlightIndex.value = filteredOptions.value.length > 0 ? 0 : -1;
        } else {
             showResults.value = false;
        }
    };
    
    const openResults = () => {
        // Al hacer focus, solo muestra si ya hay suficientes caracteres
        if (searchTerm.value.length >= props.minChars) {
            showResults.value = true;
        }
    };

    const closeResults = () => {
      // Si el input está vacío y no hay resultados, no hace nada al hacer click fuera
      // Si hay un valor seleccionado, no limpiamos el término de búsqueda, solo cerramos el dropdown
      setTimeout(() => {
          showResults.value = false;
          highlightIndex.value = -1;
      }, 150);
    };
    
    // --- NAVEGACIÓN POR TECLADO ---

    const navigateResults = (direction) => {
      if (!showResults.value || !filteredOptions.value.length) return;

      if (direction === 'down') {
        highlightIndex.value = (highlightIndex.value + 1) % filteredOptions.value.length;
      } else if (direction === 'up') {
        highlightIndex.value = (highlightIndex.value - 1 + filteredOptions.value.length) % filteredOptions.value.length;
      }
    };

    const selectHighlighted = () => {
      if (highlightIndex.value >= 0 && filteredOptions.value.length > highlightIndex.value) {
        selectOption(filteredOptions.value[highlightIndex.value]);
      } else if (filteredOptions.value.length === 1) {
          // Si solo hay un resultado y presionamos enter
          selectOption(filteredOptions.value[0]);
      }
    };

    return {
      searchTerm,
      showResults,
      highlightIndex,
      filteredOptions,
      internalSelected,
      handleInput,
      selectOption,
      clearSelection,
      closeResults,
      openResults,
      navigateResults,
      selectHighlighted,
    };
  }
};
</script>

<style scoped>
/* Estilo del componente para que se vea como un input de Argon/Bootstrap */
.argon-lookup-container {
  position: relative;
  width: 100%;
}

.results-dropdown {
  position: absolute;
  z-index: 1050; 
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.list-group-item {
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-left: none;
  border-right: none;
}

/* Integración visual con Argon Input Group */
.input-group > .form-control {
    border-top-right-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
}

.input-group > button {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border: none;
}

.list-group-item.bg-gray-200 {
    background-color: #e9ecef !important;
}

.input-group .form-control:focus {
    box-shadow: 0 0 0 0.2rem rgba(94, 114, 228, 0.25);
    border-color: #5e72e4;
}

</style>